import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useFetch } from "../../../hooks/api";
import { getHashValue } from "../../../utils/getQueryValue";
import Analytics from "../../../modules/advertising/components/Analytics";
import AnalyticsOverview from "../../../modules/advertising/components/AnalyticsOverview";
import PaymentDiscovery from "../../../views/billing/PaymentDiscovery";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { useAppSelector } from "../../../redux/hooks";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import { VStack, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import BreadcrumbDates from "../../../components/core/blocks/BreadcrumbDates";
import { DatesProvider } from "../../../dates/DatesContext";
import { Select } from "../../../components/fields/base/Select";
import { useMarketplaceOptions } from "../../../utils/marketplaceConstants";
import { useTranslation } from "react-i18next";

const AnalyticsView = () => {
  const { t } = useTranslation(["analytics"]);
  const allMarketplaceOptions = useMarketplaceOptions();
  const defaultMarketplace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);

  const autoShow = getHashValue("new_shelf", "false");
  const [selectedMenu, setSelectedMenu] = useState(autoShow.toLowerCase() === "true" ? "shelves" : "overview");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState(defaultMarketplace);
  const [shelfRedirect, setShelfRedirect] = useState(null);
  const channel = "amazon";

  const categoriesQuery = useCategoriesQuery(selectedMarketplace, channel);

  const { data: marketplaceData } = useFetch(["marketplace", channel], "/api/organizations/marketplaces");

  const initiateShelfRedirect = (id) => {
    setShelfRedirect(parseInt(id));
    setSelectedMenu("shelves");
    // Then set marketplace
  };

  const marketplaceOptions = useMemo(() => {
    const availableMarkets = Object.keys(marketplaceData?.data?.markets ?? {});
    return allMarketplaceOptions.filter((opt) => availableMarkets.includes(opt.value));
  }, [marketplaceData, allMarketplaceOptions]);

  useEffect(() => {
    if (categoriesQuery.data) {
      if (!categoriesQuery.data.some((c) => parseInt(c.id) === categoryFilter)) {
        setCategoryFilter(null);
      }
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarketplace]);

  if (!user.analytics_tier || ["unknown", "MANUALLY_DISABLED"].includes(user.analytics_tier)) {
    return (
      <div style={{ marginTop: "30px" }}>
        <PaymentDiscovery discoveryType={"search-analytics"} />
      </div>
    );
  }

  return (
    <DatesProvider>
      <BreadcrumbDates
        items={[{ name: t(($) => $.analytics["Search Analytics"]) }]}
        hideDateSelector={selectedMenu === "shelves"}
        hideCompare
      >
        <ButtonGroup
          buttons={[
            { label: t(($) => $.analytics["Overview"]), value: "overview" },
            { label: t(($) => $.analytics["Shelves"]), value: "shelves" },
          ]}
          value={selectedMenu}
          onSelect={setSelectedMenu}
          className={css({ alignSelf: "center" })}
        />
      </BreadcrumbDates>
      <PageContainer>
        <VStack gap="medium" padding="medium" alignItems="stretch">
          {selectedMenu === "shelves" ?
            <>
              <Wrap justify={"space-around"}>
                <Select
                  label={t(($) => $.analytics["Marketplace"])}
                  selectedKey={selectedMarketplace}
                  onSelectionChange={setSelectedMarketplace}
                  items={marketplaceOptions}
                  css={{ maxWidth: "200px" }}
                />

                <Select
                  label={t(($) => $.analytics["Category"])}
                  selectedKey={categoryFilter ?? "all"}
                  onSelectionChange={(key) => setCategoryFilter(key === "all" ? null : key)}
                  items={[{ value: "all", label: t(($) => $.analytics["All Categories"]) }].concat(
                    categoriesQuery.data?.map((item) => ({
                      label: item.leaf_category_name,
                      value: item.id,
                    })) ?? [],
                  )}
                  css={{ maxWidth: "200px" }}
                />
              </Wrap>

              <Analytics
                categoryFilter={categoryFilter}
                clearRedirect={setShelfRedirect}
                marketplace={selectedMarketplace}
                setCategoryFilter={setCategoryFilter}
                shelfRedirect={shelfRedirect}
              />
            </>
          : <AnalyticsOverview shelfRedirect={initiateShelfRedirect} marketplaceRedirect={setSelectedMarketplace} />}
        </VStack>
      </PageContainer>
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/searchanalytics")({
  component: AnalyticsView,
  staticData: { application: "Placement" },
});
